import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import moment from 'moment';
import { actualizarActividadConHistorial } from '../../services/actividades/actividadService';
import { listarProveedores } from '../../services/proveedores/proveedorService';
import Swal from 'sweetalert2';

const ModalEditarActividad = ({ show, onHide, actividad, onActualizar }) => {
  const [loading, setLoading] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [formData, setFormData] = useState({
    FechaInicio: '',
    FechaVencimiento: '',
    frecuencia: '',
    proveedorRef: '',
    observaciones: '',
    razonCambio: ''
  });

  useEffect(() => {
    if (actividad) {
      setFormData({
        FechaInicio: moment(actividad.FechaInicio).format('YYYY-MM-DD'),
        FechaVencimiento: moment(actividad.FechaVencimiento).format('YYYY-MM-DD'),
        frecuencia: actividad.frecuencia || '',
        proveedorRef: actividad.proveedorRef?._id || '',
        observaciones: actividad.observaciones || '',
        razonCambio: ''
      });
    }
  }, [actividad]);

  useEffect(() => {
    cargarProveedores();
  }, []);

  const cargarProveedores = async () => {
    try {
      const response = await listarProveedores();
      if (response?.data.data) {
        const options = response.data.data
          .filter(p => p.estado === 'Activo')
          .map(p => ({ value: p._id, label: p.nombre }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setProveedores(options);
      }
    } catch (error) {
      console.error('Error al cargar proveedores:', error);
    }
  };

  const handleChange = (campo, valor) => {
    setFormData(prev => ({ ...prev, [campo]: valor }));
  };

  const validarFormulario = () => {
    if (!formData.FechaInicio) {
      Swal.fire({ icon: 'warning', text: 'La fecha de inicio es requerida' });
      return false;
    }
    if (!formData.FechaVencimiento) {
      Swal.fire({ icon: 'warning', text: 'La fecha de vencimiento es requerida' });
      return false;
    }
    if (!formData.frecuencia) {
      Swal.fire({ icon: 'warning', text: 'La frecuencia es requerida' });
      return false;
    }
    if (!formData.razonCambio || formData.razonCambio.trim() === '') {
      Swal.fire({ icon: 'warning', text: 'Debe indicar la razón del cambio' });
      return false;
    }
    
    // Validar que fecha vencimiento sea posterior a fecha inicio
    if (moment(formData.FechaVencimiento).isBefore(formData.FechaInicio)) {
      Swal.fire({ icon: 'warning', text: 'La fecha de vencimiento debe ser posterior a la fecha de inicio' });
      return false;
    }

    return true;
  };

  const handleGuardar = async () => {
    if (!validarFormulario()) return;

    try {
      setLoading(true);
      
      const datosActualizar = {
        FechaInicio: formData.FechaInicio,
        FechaVencimiento: formData.FechaVencimiento,
        frecuencia: formData.frecuencia,
        proveedorRef: formData.proveedorRef || null,
        observaciones: formData.observaciones,
        razonCambio: formData.razonCambio
      };

      const response = await actualizarActividadConHistorial(actividad._id, datosActualizar);
      
      if (response) {
        Swal.fire({ icon: 'success', text: 'Actividad actualizada correctamente' });
        onActualizar();
      }
    } catch (error) {
      console.error('Error al actualizar actividad:', error);
      Swal.fire({ icon: 'error', text: error.response?.data?.message || 'Error al actualizar la actividad' });
    } finally {
      setLoading(false);
    }
  };

  const opcionesFrecuencia = [
    { value: 'Mensual', label: 'Mensual' },
    { value: 'Bimestral', label: 'Bimestral' },
    { value: 'Trimestral', label: 'Trimestral' },
    { value: 'Cuatrimestral', label: 'Cuatrimestral' },
    { value: 'Semestral', label: 'Semestral' },
    { value: 'Anual', label: 'Anual' },
    { value: 'Bianual', label: 'Bianual' },
    { value: 'Única', label: 'Única' }
  ].sort((a, b) => a.label.localeCompare(b.label));

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Editar Actividad</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{maxHeight:'80vh', overflowY:'auto'}}
      >
        <Row>
          <Col xs={12} className="mb-3">
            <Form.Label className="text-muted">Equipo</Form.Label>
            <Form.Control
              type="text"
              value={actividad?.equipo?.item || 'N/A'}
              disabled
              readOnly
            />
          </Col>

          <Col xs={12} md={6} className="mb-3">
            <Form.Label className="text-muted">Tipo de Actividad</Form.Label>
            <Form.Control
              type="text"
              value={actividad?.tipoActividad || 'N/A'}
              disabled
              readOnly
            />
          </Col>

          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Fecha Inicio <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="date"
              value={formData.FechaInicio}
              onChange={(e) => handleChange('FechaInicio', e.target.value)}
            />
          </Col>

          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Fecha Vencimiento <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="date"
              value={formData.FechaVencimiento}
              onChange={(e) => handleChange('FechaVencimiento', e.target.value)}
            />
          </Col>

          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Frecuencia <span className="text-danger">*</span></Form.Label>
            <Select
              options={opcionesFrecuencia}
              value={opcionesFrecuencia.find(f => f.value === formData.frecuencia)}
              onChange={(option) => handleChange('frecuencia', option?.value || '')}
              placeholder="Seleccionar frecuencia..."
            />
          </Col>

          <Col xs={12} md={6} className="mb-3">
            <Form.Label>Proveedor</Form.Label>
            <Select
              options={proveedores}
              value={proveedores.find(p => p.value === formData.proveedorRef)}
              onChange={(option) => handleChange('proveedorRef', option?.value || '')}
              placeholder="Buscar proveedor..."
              isClearable
            />
          </Col>

          <Col xs={12} className="mb-3">
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.observaciones}
              onChange={(e) => handleChange('observaciones', e.target.value)}
              placeholder="Observaciones adicionales..."
            />
          </Col>

          <Col xs={12} className="mb-3">
            <Form.Label>Razón del Cambio <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.razonCambio}
              onChange={(e) => handleChange('razonCambio', e.target.value)}
              placeholder="Indique la razón por la cual se está modificando esta actividad..."
              className="border-warning"
            />
            <Form.Text className="text-muted">
              Este campo es obligatorio y quedará registrado en el historial de cambios
            </Form.Text>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleGuardar} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Guardando...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-2"></i>
              Guardar Cambios
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalEditarActividad;
